import { Injectable } from '@angular/core';
import { SalesOrder } from '@shared/entities/salesOrder';
import { ApiService } from '@shared/services';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Attachment } from '@shared/entities/attachment';
import { SalesOrderService } from './sales-order.service';

const LOG_PREFIX = '[Api][SalesOrderForm] ';

@Injectable({
  providedIn: 'root'
})
export class SalesOrderFormService extends ApiService<SalesOrder> {
  constructor(private http: HttpClient, private _salesOrderService: SalesOrderService) {
    const masterUrl = '/sales-orders';
    const detailUrl = '/sales-orders';

    super(SalesOrder, masterUrl, detailUrl);
  }

  findSalesOrder(id: string): Observable<SalesOrder | null> {
    this.removeUrlParameters();
    this.setUrlParameter('id', id);

    return this.paginate(SalesOrder, 1, 1).pipe(
      map((paginate) => {
        if (paginate && paginate.data && paginate.data.length > 0) {
          return paginate.data[0];
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findSalesOrder error', error);
        return of(null);
      })
    );
  }

  saveSalesOrder(salesOrderData: SalesOrder): Observable<SalesOrder | null> {
    this.logger.debug(LOG_PREFIX + 'saveSalesOrder', salesOrderData);
    this.removeUrlParameters();

    const salesOrder = Object.assign(new SalesOrder(), salesOrderData);

    return this.save(salesOrder).pipe(
      map((response) => {
        this.logger.debug(LOG_PREFIX + 'saveSalesOrder success', response);
        this._salesOrderService.refresh();
        return response || salesOrder;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveSalesOrder error', error);
        return of(error);
      })
    );
  }

  editSalesOrder(salesOrderData: SalesOrder, salesOrderId: string): Observable<SalesOrder | null> {
    this.logger.debug(LOG_PREFIX + `editSalesOrder with id: ${salesOrderId}`, salesOrderData);
    this.removeUrlParameters();

    return this.save(salesOrderData, salesOrderId).pipe(
      map((response) => {
        this.logger.debug(LOG_PREFIX + 'editSalesOrder success', response);
        this._salesOrderService.refresh();
        return response || salesOrderData;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'editSalesOrder error', error);
        return of(error);
      })
    );
  }

  findByFilter(id: string): Observable<SalesOrder | null> {
    this.removeUrlParameters();
    this.setUrlParameter('id', id);

    return this.paginate(SalesOrder, 1, 1).pipe(
      map((paginate) => {
        if (paginate && paginate.data && paginate.data.length > 0) {
          return paginate.data[0];
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findByFilter error', error);
        return of(null);
      })
    );
  }

  deleteSalesOrder(id: string): Observable<boolean | null> {
    this.logger.debug(LOG_PREFIX + `deleteSalesOrder with id: ${id}`);
    this.setUrlParameter('id', id);
    return this.remove(id).pipe(
      map(() => {
        this.logger.debug(LOG_PREFIX + 'deleteSalesOrder success');
        this._salesOrderService.refresh();
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteSalesOrder error', error);
        return of(error);
      })
    );
  }

  uploadAttachment(file: File | FormData): Observable<Attachment> {
    this.removeUrlParameters();

    const formData = this.createFormData(file);

    return this.postMultipartData<Attachment>('/attachments', formData, Attachment).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'uploadAttachment error', error);
        throw error;
      })
    );
  }

  deleteFile(fileId: string): Observable<boolean | null> {
    return this.http.delete<boolean>(`/attachments/${fileId}`).pipe(
      map(() => {
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteFile error', error);
        return of(null);
      })
    );
  }

  saveSalesOrderWithAttachments(salesOrderData: SalesOrder, files: Record<string, File>): Observable<SalesOrder> {
    this.logger.debug(LOG_PREFIX + 'saveSalesOrderWithAttachments', {
      salesOrderData,
      fileCount: Object.keys(files).length
    });
    this.removeUrlParameters();

    const formData = new FormData();
    formData.append('request', JSON.stringify(salesOrderData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.postMultipartData<SalesOrder>('/sales-orders', formData, SalesOrder).pipe(
      tap((salesOrder) => {
        this.logger.debug(LOG_PREFIX + 'saveSalesOrderWithAttachments success', salesOrder);
        this._salesOrderService.refresh();
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveSalesOrderWithAttachments error', error);
        throw error;
      })
    );
  }

  updateSalesOrderWithAttachments(
    salesOrderId: string,
    salesOrderData: SalesOrder,
    files: Record<string, File>
  ): Observable<SalesOrder> {
    this.logger.debug(LOG_PREFIX + 'updateSalesOrderWithAttachments', {
      salesOrderId,
      salesOrderData,
      fileCount: Object.keys(files).length
    });
    this.removeUrlParameters();

    const formData = new FormData();
    salesOrderData.id = salesOrderId;
    formData.append('request', JSON.stringify(salesOrderData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.putMultipartData<SalesOrder>('/sales-orders', formData, SalesOrder).pipe(
      tap((salesOrder) => {
        this.logger.debug(LOG_PREFIX + 'updateSalesOrderWithAttachments success', salesOrder);
        this._salesOrderService.refresh();
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'updateSalesOrderWithAttachments error', error);
        throw error;
      })
    );
  }

  private createFormData(fileOrFormData: File | FormData): FormData {
    if (fileOrFormData instanceof FormData) {
      return fileOrFormData;
    }

    const formData = new FormData();
    formData.append('file', fileOrFormData, fileOrFormData.name);
    return formData;
  }

  uploadMultipleAttachments(files: File[]): Observable<Attachment[]> {
    this.removeUrlParameters();

    if (files.length === 0) {
      return of([]);
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return this.postMultipartDataArray<Attachment>('/attachments/batch', formData, Attachment).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'uploadMultipleAttachments error', error);
        throw error;
      })
    );
  }
}
